/**
 * Patient History Service
 * 
 * This service handles all API calls related to patient appointment history,
 * including fetching comprehensive appointment data with vitals, notes, and medications.
 */

import apiClient from './apiClient';

class PatientHistoryService {
  
  /**
   * Get complete appointment history for a patient
   * @param {number} numericPatientId - Numeric ID of the patient (database primary key)
   * @param {number} limit - Maximum number of appointments to return (default: 50)
   * @returns {Promise} Promise resolving to appointment history data
   */
  async getPatientHistory(numericPatientId, limit = 50) {
    try {
      if (!numericPatientId || typeof numericPatientId !== 'number') {
        throw new Error('Patient ID must be a numeric value (database primary key)');
      }
      
      console.log(`🔍 Fetching appointment history for patient ID: ${numericPatientId}`);
      
      const response = await apiClient.get(`/patient-history-test/patients/${numericPatientId}/history`, {
        params: { limit }
      });
      
      console.log(`✅ Retrieved ${response.data.appointments?.length || 0} appointments for patient ${numericPatientId}`);
      return response.data;
      
    } catch (error) {
      console.error('Error fetching patient history:', error);
      throw new Error(this.getErrorMessage(error));
    }
  }

  /**
   * Get detailed information for a specific appointment
   * @param {number} numericPatientId - Numeric ID of the patient (database primary key)
   * @param {number} bookingId - ID of the specific booking/appointment
   * @returns {Promise} Promise resolving to appointment details
   */
  async getAppointmentDetails(numericPatientId, bookingId) {
    try {
      if (!numericPatientId || typeof numericPatientId !== 'number') {
        throw new Error('Patient ID must be a numeric value (database primary key)');
      }
      
      console.log(`🔍 Fetching appointment details - Patient: ${numericPatientId}, Booking: ${bookingId}`);
      
      const response = await apiClient.get(`/patient-history-test/patients/${numericPatientId}/appointments/${bookingId}`);
      
      console.log(`✅ Retrieved appointment details for booking ${bookingId}`);
      return response.data;
      
    } catch (error) {
      console.error('Error fetching appointment details:', error);
      throw new Error(this.getErrorMessage(error));
    }
  }

  /**
   * Get summary statistics for a patient's appointment history
   * @param {number} numericPatientId - Numeric ID of the patient (database primary key)
   * @returns {Promise} Promise resolving to summary statistics
   */
  async getPatientHistorySummary(numericPatientId) {
    try {
      if (!numericPatientId || typeof numericPatientId !== 'number') {
        throw new Error('Patient ID must be a numeric value (database primary key)');
      }
      
      console.log(`🔍 Fetching history summary for patient ID: ${numericPatientId}`);
      
      const response = await apiClient.get(`/patient-history-test/patients/${numericPatientId}/history/summary`);
      
      console.log(`✅ Retrieved history summary for patient ${numericPatientId}`);
      return response.data;
      
    } catch (error) {
      console.error('Error fetching patient history summary:', error);
      throw new Error(this.getErrorMessage(error));
    }
  }

  /**
   * Test API connectivity
   * @returns {Promise} Promise resolving to connection test results
   */
  async testConnection() {
    try {
      const response = await apiClient.get('/patient-history-test/test/connection');
      return response.data;
    } catch (error) {
      console.error('Error testing connection:', error);
      throw new Error(this.getErrorMessage(error));
    }
  }

  // Helper methods for data formatting

  /**
   * Format vitals data for display
   * @param {Object} vitals - Raw vitals data from API
   * @returns {Object} Formatted vitals object
   */
  formatVitals(vitals) {
    if (!vitals) return null;
    
    return {
      temperature: vitals.temperature ? `${vitals.temperature}${vitals.temperature_unit || '°C'}` : null,
      pulseRate: vitals.pulse_rate ? `${vitals.pulse_rate}/min` : null,
      spo2: vitals.spo2 ? `${vitals.spo2}%` : null,
      bloodPressure: (vitals.systolic_bp && vitals.diastolic_bp) ? 
        `${vitals.systolic_bp}/${vitals.diastolic_bp}mmHg` : null,
      respiratoryRate: vitals.respiratory_rate ? `${vitals.respiratory_rate}/min` : null,
      glucose: vitals.glucose ? `${vitals.glucose} ${vitals.glucose_unit || 'mg/dL'}` : null,
      height: vitals.height ? `${vitals.height}${vitals.height_unit || 'cm'}` : null,
      weight: vitals.weight ? `${vitals.weight}${vitals.weight_unit || 'kg'}` : null,
      bmi: vitals.bmi ? `${vitals.bmi}` : null,
      // Raw values for calculations
      temperatureValue: vitals.temperature,
      pulseRateValue: vitals.pulse_rate,
      spo2Value: vitals.spo2,
      systolicBp: vitals.systolic_bp,
      diastolicBp: vitals.diastolic_bp,
      respiratoryRateValue: vitals.respiratory_rate,
      glucoseValue: vitals.glucose,
      heightValue: vitals.height,
      weightValue: vitals.weight,
      bmiValue: vitals.bmi,
      // Equipment data
      spirometryData: vitals.spirometry_data,
      stethoscopeData: vitals.stethoscope_data,
      ecgData: vitals.ecg_data,
      otoscopeData: vitals.otoscope_data,
      notes: vitals.notes,
      recordedAt: vitals.recorded_at
    };
  }

  /**
   * Format appointment date for display
   * @param {string} dateStr - ISO date string
   * @returns {string} Formatted date string
   */
  formatAppointmentDate(dateStr) {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (error) {
      console.warn('Error formatting date:', dateStr, error);
      return dateStr;
    }
  }

  /**
   * Format appointment time for display
   * @param {string} timeStr - ISO time string
   * @returns {string} Formatted time string
   */
  formatAppointmentTime(timeStr) {
    try {
      // Handle time-only strings
      if (timeStr && timeStr.includes(':')) {
        const [hours, minutes] = timeStr.split(':');
        const date = new Date();
        date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        return date.toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true 
        });
      }
      return timeStr;
    } catch (error) {
      console.warn('Error formatting time:', timeStr, error);
      return timeStr;
    }
  }

  /**
   * Get appointment type badge color
   * @param {string} appointmentType - Type of appointment
   * @returns {string} CSS color class
   */
  getAppointmentTypeBadgeColor(appointmentType) {
    switch (appointmentType?.toLowerCase()) {
      case 'general telehealth':
        return 'bg-blue-100 text-blue-800';
      case 'clinical telehealth':
        return 'bg-green-100 text-green-800';
      case 'urgent':
        return 'bg-red-100 text-red-800';
      case 'routine':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  }

  /**
   * Sort appointments by date (most recent first)
   * @param {Array} appointments - Array of appointment objects
   * @returns {Array} Sorted appointments
   */
  sortAppointmentsByDate(appointments) {
    if (!Array.isArray(appointments)) return [];
    
    return appointments.sort((a, b) => {
      const dateA = new Date(a.booking?.appointment_date || 0);
      const dateB = new Date(b.booking?.appointment_date || 0);
      return dateB - dateA; // Most recent first
    });
  }

  /**
   * Get the most recent vital signs values
   * @param {Array} appointments - Array of appointment objects
   * @returns {Object|null} Most recent vitals or null
   */
  getMostRecentVitals(appointments) {
    if (!Array.isArray(appointments) || appointments.length === 0) return null;
    
    const sortedAppointments = this.sortAppointmentsByDate(appointments);
    
    for (const appointment of sortedAppointments) {
      if (appointment.vitals) {
        return this.formatVitals(appointment.vitals);
      }
    }
    
    return null;
  }

  /**
   * Extract error message from API error response
   * @param {Error} error - Error object from API call
   * @returns {string} Human-readable error message
   */
  getErrorMessage(error) {
    // Handle Axios errors with response
    if (error.response) {
      const status = error.response.status;
      const data = error.response.data;
      
      // Handle specific status codes
      switch (status) {
        case 422:
          return `Invalid request: ${data?.detail || 'Please check the patient ID format'}`;
        case 404:
          return `Patient not found: ${data?.detail || 'No appointment history available'}`;
        case 403:
          return `Access denied: ${data?.detail || 'You do not have permission to view this data'}`;
        case 500:
          return `Server error: ${data?.detail || 'Please try again later'}`;
        default:
          if (data?.detail) {
            return data.detail;
          }
          if (data?.message) {
            return data.message;
          }
          return `Request failed with status ${status}`;
      }
    }
    
    // Handle network errors
    if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
      return 'Network error. Please check your connection and ensure the backend server is running.';
    }
    
    // Handle timeout errors
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      return 'Request timeout. Please try again.';
    }
    
    // Default error message
    if (error.message) {
      return error.message;
    }
    
    return 'An unexpected error occurred while fetching appointment history';
  }

  /**
   * Check if appointment has equipment data available
   * @param {Object} vitals - Vitals object
   * @param {string} equipmentType - Type of equipment (spirometry, stethoscope, ecg, otoscope)
   * @returns {boolean} True if equipment data is available
   */
  hasEquipmentData(vitals, equipmentType) {
    if (!vitals) return false;
    
    const dataField = `${equipmentType}_data`;
    return !!(vitals[dataField] && vitals[dataField].trim());
  }

  /**
   * Group appointments by date for display
   * @param {Array} appointments - Array of appointment objects
   * @returns {Object} Appointments grouped by date
   */
  groupAppointmentsByDate(appointments) {
    if (!Array.isArray(appointments)) return {};
    
    return appointments.reduce((groups, appointment) => {
      const date = appointment.booking?.appointment_date;
      if (date) {
        const formattedDate = this.formatAppointmentDate(date);
        if (!groups[formattedDate]) {
          groups[formattedDate] = [];
        }
        groups[formattedDate].push(appointment);
      }
      return groups;
    }, {});
  }
}

// Export a singleton instance
export const patientHistoryService = new PatientHistoryService();
export default patientHistoryService;
